/* Afnan travel sets landing page.
   One script for every afnan-travel section. The possibility board is the single source of bottles;
   the belt hero, loose miniatures and closing links read from it. Re-initialises on Theme Editor changes. */
(() => {
  'use strict';
  if (window.AfnanTravel) return;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canDrag = () => matchMedia('(min-width: 900px) and (hover: hover)').matches;
  const pad = n => String(n).padStart(2, '0');
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  let controller = null;
  let observers = [];
  let board = null; // API of the possibility board on this page, or null
  let data = null;

  function readData() {
    const el = $('[data-atp-board] [data-atp-data]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  const bottleAt = (si, k) => data?.sets?.[si]?.bottles?.[k] || null;

  /* Tape strips must sit below the site header (it can be a transparent overlay at the top of the page) but above the
     neighbouring sections: read the header's own layer and put the tape one below it. */
  function fitTapeLayer() {
    try {
      const header = $('.shopify-section--header, #shopify-section-header, header');
      let el = header, z = NaN;
      while (el && el !== document.documentElement && !Number.isFinite(z)) {
        z = parseInt(getComputedStyle(el).zIndex, 10);
        el = el.parentElement;
      }
      if (Number.isFinite(z)) document.documentElement.style.setProperty('--atp-tape-z', String(Math.max(0, Math.min(z - 1, 4))));
    } catch (e) { /* keep the CSS default */ }
  }

  /* Small screens: the board is long, so after a tap bring the content that changed into view, clear of any sticky header.
     Only on a tap or click (not arrow keys). */
  const smallScreen = matchMedia('(max-width: 899px)');
  function bringIntoView(el) {
    if (!el || !smallScreen.matches) return;
    const top = el.getBoundingClientRect().top;
    const margin = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0) + 12;
    if (Math.abs(top - margin) < 24) return; /* already there */
    window.scrollTo({ top: window.scrollY + top - margin, behavior: reduce ? 'auto' : 'smooth' });
  }

  /* ---------- Possibility board ---------- */
  function initBoard(root, on) {
    if (!root) return null;
    const tabsEl = $('[data-atp-tabs]', root);
    const tabs = $$('[data-atp-tab]', root);
    const panels = $$('[data-atp-panel]', root);
    const notes = $$('[data-atp-note]', root);
    const threads = $('[data-atp-threads]', root);
    if (!tabs.length) return null;
    let current = Math.max(0, tabs.findIndex(t => t.getAttribute('aria-selected') === 'true'));
    const chosen = panels.map(() => 0);

    const minisOf = i => $$('[data-atp-mini]', panels[i]);
    const shelfOf = i => $('[data-atp-shelf]', panels[i]);

    function centerIn(rail, el, smooth) {
      if (!rail || !el || rail.scrollWidth <= rail.clientWidth + 2) return;
      rail.scrollTo({ left: el.offsetLeft - (rail.clientWidth - el.offsetWidth) / 2, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    }

    function selectBottle(k, scroll = true) {
      const minis = minisOf(current);
      if (!minis.length) return;
      k = (k + minis.length) % minis.length;
      chosen[current] = k;
      minis.forEach((m, j) => m.setAttribute('aria-pressed', String(j === k)));
      const p = panels[current];
      const name = $('[data-atp-name]', p);
      if (name) name.textContent = minis[k].dataset.name || '';
      $$('[data-atp-notes]', p).forEach(n => { n.hidden = Number(n.dataset.atpNotes) !== k; });
      if (scroll) centerIn(shelfOf(current), minis[k], true);
      queueThreads();
    }

    function showSet(i, focusTab = false) {
      i = (i + tabs.length) % tabs.length;
      const changed = i !== current;
      current = i;
      tabs.forEach((t, j) => { t.setAttribute('aria-selected', String(j === i)); t.tabIndex = j === i ? 0 : -1; });
      panels.forEach((p, j) => { p.hidden = j !== i; });
      notes.forEach((n, j) => {
        n.hidden = j !== i;
        if (j === i && changed && !reduce) { n.classList.remove('is-swapping'); void n.offsetWidth; n.classList.add('is-swapping'); }
      });
      centerIn(tabsEl, tabs[i], true);
      selectBottle(chosen[i], false);
      centerIn(shelfOf(i), minisOf(i)[chosen[i]], false);
      if (focusTab) tabs[i].focus();
      queueThreads();
    }

    /* tap a set: show its panel, then bring its bottles into view */
    tabs.forEach((t, i) => t.addEventListener('click', () => { showSet(i); bringIntoView(panels[i]); }, on));
    tabsEl.addEventListener('keydown', e => {
      const map = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (e.key === 'Home') { e.preventDefault(); showSet(0, true); }
      else if (e.key === 'End') { e.preventDefault(); showSet(tabs.length - 1, true); }
      else if (map[e.key]) {
        e.preventDefault();
        const flip = document.dir === 'rtl' && /Left|Right/.test(e.key) ? -1 : 1;
        showSet(current + map[e.key] * flip, true);
      }
    }, on);
    tabsEl.addEventListener('scroll', queueThreads, { ...on, passive: true });

    panels.forEach((p, i) => {
      /* tap a bottle: select it, then bring its name and fragrance families into view */
      minisOf(i).forEach((m, k) => m.addEventListener('click', () => { selectBottle(k); bringIntoView($('.atp-foot', p)); }, on));
      $$('[data-atp-step]', p).forEach(b => b.addEventListener('click', () => selectBottle(chosen[i] + Number(b.dataset.atpStep)), on));
      const shelf = shelfOf(i);
      if (!shelf) return;
      shelf.addEventListener('keydown', e => {
        const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        const minis = minisOf(i);
        /* on small screens the cards wrap into rows: up and down move a row, without wrapping round */
        const cols = minis.filter(m => m.offsetTop === minis[0].offsetTop).length;
        const v = cols < minis.length ? { ArrowDown: cols, ArrowUp: -cols }[e.key] : 0;
        if (v) {
          e.preventDefault();
          const k = chosen[i] + v;
          if (k < 0 || k >= minis.length) return;
          selectBottle(k);
          minis[k].focus();
          return;
        }
        if (!d) return;
        e.preventDefault();
        selectBottle(chosen[i] + (document.dir === 'rtl' ? -d : d));
        minis[chosen[i]].focus({ preventScroll: true });
      }, on);
      /* on phones the shelf scrolls: the bottle that settles in the middle is selected */
      let t;
      shelf.addEventListener('scroll', () => {
        queueThreads();
        clearTimeout(t);
        t = setTimeout(() => {
          if (shelf.scrollWidth <= shelf.clientWidth + 2) return;
          const mid = shelf.scrollLeft + shelf.clientWidth / 2;
          let best = 0, dist = Infinity;
          minisOf(i).forEach((m, j) => { const d = Math.abs(m.offsetLeft + m.offsetWidth / 2 - mid); if (d < dist) { dist = d; best = j; } });
          if (best !== chosen[i]) selectBottle(best, false);
        }, 90);
      }, { ...on, passive: true });
    });

    /* Thread between the pins, drawn from where they actually are */
    let frame = 0;
    function queueThreads() { cancelAnimationFrame(frame); frame = requestAnimationFrame(drawThreads); }
    function drawThreads() {
      if (!threads) return;
      const base = threads.getBoundingClientRect();
      if (!base.width) return;
      threads.setAttribute('viewBox', `0 0 ${base.width} ${base.height}`);
      const point = el => {
        if (!el || el.closest('[hidden]')) return null;
        const r = el.getBoundingClientRect(), x = r.left + r.width / 2, y = r.top + r.height / 2;
        const rail = el.closest('[data-atp-tabs], [data-atp-shelf]');
        if (rail) { const c = rail.getBoundingClientRect(); if (x < c.left + 4 || x > c.right - 4) return null; }
        return { x: x - base.left, y: y - base.top };
      };
      const paths = [];
      const connect = (a, b, active) => {
        if (!a || !b) return;
        const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
        if (len < 18) return;
        const s = { x: a.x + dx / len * 6, y: a.y + dy / len * 6 }, e = { x: b.x - dx / len * 6, y: b.y - dy / len * 6 };
        const sag = Math.min(18, len * .06);
        paths.push(`<path class="atp-thread${active ? ' atp-thread--active' : ''}" d="M${s.x.toFixed(1)} ${s.y.toFixed(1)} Q${((s.x + e.x) / 2).toFixed(1)} ${((s.y + e.y) / 2 + sag).toFixed(1)} ${e.x.toFixed(1)} ${e.y.toFixed(1)}"/>`);
      };
      /* pins in a row are joined; when the cards wrap onto a new row (small screens) the thread stops there */
      const sameRow = (a, b) => a && b && Math.abs(b.y - a.y) < 40;
      const tabPins = tabs.map(t => point($('.atp-pin', t)));
      for (let i = 1; i < tabPins.length; i++) if (sameRow(tabPins[i - 1], tabPins[i])) connect(tabPins[i - 1], tabPins[i]);
      const p = panels[current];
      const minis = minisOf(current).map(m => point($('.atp-pin', m)));
      const vis = minis.filter(Boolean);
      connect(tabPins[current], vis[0], true);
      for (let i = 1; i < vis.length; i++) if (sameRow(vis[i - 1], vis[i])) connect(vis[i - 1], vis[i]);
      const foot = point($('[data-atp-foot-pin]', p));
      connect(minis[chosen[current]], foot, true);
      connect(foot, point($('[data-atp-note-pin]', notes[current])), true);
      threads.innerHTML = paths.join('');
    }
    const ro = new ResizeObserver(queueThreads);
    ro.observe($('.atp-board', root));
    observers.push(ro);
    addEventListener('load', queueThreads, on);
    document.fonts?.ready.then(queueThreads);

    /* Add set to cart, using the Prestige cart contract so the theme's cart drawer and count update */
    $$('form[data-atp-add]', root).forEach(form => form.addEventListener('submit', e => { e.preventDefault(); addToCart(form, root); }, on));

    /* Theme Editor: selecting a set or bottle block shows it */
    document.addEventListener('shopify:block:select', e => {
      const el = e.target;
      if (!root.contains(el)) return;
      const tab = el.closest('[data-atp-tab]');
      if (tab) return showSet(tabs.indexOf(tab));
      const mini = el.closest('[data-atp-mini]');
      if (mini) { const pi = panels.indexOf(mini.closest('[data-atp-panel]')); showSet(pi); selectBottle(Number(mini.dataset.atpMini)); }
    }, on);

    showSet(current);
    return {
      root,
      openSet(i) { showSet(i); root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); },
      openBottle(i, k) {
        showSet(i); selectBottle(k, false);
        root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        setTimeout(() => { centerIn(shelfOf(current), minisOf(current)[k], true); minisOf(current)[k]?.focus({ preventScroll: true }); }, reduce ? 0 : 500);
      },
      refresh: queueThreads
    };
  }

  async function addToCart(form, root) {
    const button = $('button[type="submit"]', form);
    const label = $('[data-atp-add-label]', form);
    const status = $('[data-atp-add-status]', form);
    const d = root.dataset;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const setName = $('.atp-setnote__title', form.closest('[data-atp-note]'))?.textContent.trim() || '';
    button.setAttribute('aria-busy', 'true');
    label.textContent = d.labelAdding;
    status.textContent = ''; status.classList.remove('is-error');

    const routesRoot = window.Shopify?.routes?.root || ((d.rootUrl || '/').replace(/\/?$/, '/'));
    const formData = new FormData(form);
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    if (sections.length) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', `${routesRoot}variants/${formData.get('id')}`);
    }

    try {
      const response = await fetch(`${d.cartAddUrl || routesRoot + 'cart/add'}.js`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const json = await response.json();
      if (!response.ok) {
        const message = json.description || json.message || d.labelError;
        status.textContent = typeof message === 'string' ? message : d.labelError;
        status.classList.add('is-error');
        label.textContent = d.labelAdd;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      const cart = await (await fetch(`${routesRoot}cart.js`, { headers: { Accept: 'application/json' } })).json();
      cart.sections = json.sections;
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: null, cart } }));
      label.textContent = d.labelAdded;
      status.textContent = (d.labelAddedStatus || '').replace('[name]', setName);
      if (d.afterAdd === 'cart_page') { window.location.href = d.cartUrl || `${routesRoot}cart`; return; }
      setTimeout(() => { label.textContent = d.labelAdd; }, 2200);
    } catch (err) {
      status.textContent = d.labelError;
      status.classList.add('is-error');
      label.textContent = d.labelAdd;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /* ---------- Belt hero ---------- */
  function initBelts(on) {
    $$('[data-atp-hero]').forEach(hero => {
      const belt = $('[data-atp-belt]', hero), track = $('[data-atp-belt-track]', hero), toggle = $('[data-atp-belt-toggle]', hero);
      const flat = [];
      const sets = data?.sets || [];
      const longest = Math.max(0, ...sets.map(s => s.bottles.length));
      for (let k = 0; k < longest; k++) sets.forEach((s, si) => { const b = s.bottles[k]; if (b && b.img) flat.push({ ...b, si, k, set: s }); });
      const empty = $('[data-atp-belt-empty]', hero);
      if (!flat.length) { track.innerHTML = ''; if (empty) empty.hidden = false; if (toggle) toggle.hidden = true; return; }
      if (empty) empty.hidden = true;
      const bag = (it, copy) => `<button class="atp-bag" type="button" data-set="${it.si}" data-bottle="${it.k}" style="--tilt:${((it.si * 7 + it.k * 5) % 9) - 4}deg"${copy ? ' tabindex="-1" aria-hidden="true"' : ` aria-label="${esc(it.name)}, ${esc(it.set.name)}"`}>
        <span class="atp-bag__slot"><img src="${esc(it.img)}" alt="" width="260" height="712" decoding="async" draggable="false"></span><span class="atp-bag__belt" aria-hidden="true"></span>
        <span class="atp-bag__tag"><b>${esc(it.name)}</b><small>${esc(it.set.short)}</small></span></button>`;
      track.innerHTML = flat.map(it => bag(it, false)).join('') + flat.map(it => bag(it, true)).join('');
      /* The belt runs on its own and can be dragged (mouse or touch) to move it faster; a flick carries on for a moment */
      const duration = parseFloat(getComputedStyle(hero).getPropertyValue('--atp-speed')) || 120;
      let x = 0, half = 0, last = performance.now(), hover = false, paused = reduce, dragging = false, velocity = 0;
      let startX = 0, startOffset = 0, moved = false, lastMoveX = 0, lastMoveT = 0, frame = 0;
      const measure = () => { half = track.scrollWidth / 2; };
      const wrap = () => { if (half) x = ((x % half) - half) % half; }; /* keeps x within (-half, 0] */
      const tick = now => {
        const dt = Math.min(64, now - last); last = now;
        if (!dragging) {
          if (Math.abs(velocity) > .02) { x += velocity * dt; velocity *= Math.pow(.94, dt / 16); }
          else if (!paused && !hover) x -= half / (duration * 1000) * dt;
          wrap();
        }
        track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`;
        frame = requestAnimationFrame(tick);
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(track);
      observers.push({ disconnect: () => { ro.disconnect(); cancelAnimationFrame(frame); } });
      frame = requestAnimationFrame(tick);

      belt.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') hover = true; }, on);
      belt.addEventListener('pointerleave', () => { hover = false; }, on);
      belt.addEventListener('pointerdown', e => {
        if (e.button !== 0) return;
        dragging = true; moved = false; velocity = 0;
        startX = lastMoveX = e.clientX; startOffset = x; lastMoveT = performance.now();
      }, on);
      belt.addEventListener('pointermove', e => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return;
        if (!moved) { moved = true; belt.classList.add('is-dragging'); try { belt.setPointerCapture(e.pointerId); } catch (err) {} }
        const now = performance.now();
        velocity = (e.clientX - lastMoveX) / Math.max(1, now - lastMoveT);
        lastMoveX = e.clientX; lastMoveT = now;
        x = startOffset + dx; wrap();
      }, on);
      const release = () => {
        if (!dragging) return;
        dragging = false; belt.classList.remove('is-dragging');
        if (performance.now() - lastMoveT > 80) velocity = 0;
        velocity = Math.max(-3, Math.min(3, velocity));
      };
      belt.addEventListener('pointerup', release, on);
      belt.addEventListener('pointercancel', release, on);
      /* a drag is not a click */
      belt.addEventListener('click', e => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, { ...on, capture: true });
      belt.addEventListener('focusin', () => { hover = true; }, on);
      belt.addEventListener('focusout', () => { hover = false; }, on);

      $$('.atp-bag', track).forEach(el => el.addEventListener('click', () => board?.openBottle(Number(el.dataset.set), Number(el.dataset.bottle)), on));
      if (toggle) {
        toggle.hidden = reduce;
        toggle.addEventListener('click', () => {
          paused = !paused;
          toggle.setAttribute('aria-pressed', String(paused));
          toggle.textContent = paused ? toggle.dataset.play : toggle.dataset.pause;
        }, on);
      }
    });
  }

  /* ---------- Loose miniatures ---------- */
  function initLoose(on) {
    $$('[data-atp-loose]').forEach(el => {
      const si = Number(el.dataset.set), k = Number(el.dataset.bottle), it = bottleAt(si, k);
      if (!it || !it.img) { el.hidden = true; return; }
      el.innerHTML = `<img src="${esc(it.img)}" alt="" width="260" height="712" loading="lazy" draggable="false"><span class="atp-loose__tag">${esc(it.name)} ↗</span>`;
      el.setAttribute('aria-label', `${it.name}, ${data.sets[si].name}`);
      el.hidden = false;
      let sx, sy, ox, oy, down = false, moved = false;
      el.addEventListener('pointerdown', e => {
        e.stopPropagation();
        if (!canDrag()) return;
        down = true; moved = false; sx = e.clientX; sy = e.clientY;
        ox = parseFloat(el.style.getPropertyValue('--dx')) || 0; oy = parseFloat(el.style.getPropertyValue('--dy')) || 0;
        el.setPointerCapture(e.pointerId);
      }, on);
      el.addEventListener('pointermove', e => {
        if (!down) return;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (!moved && Math.hypot(dx, dy) < 6) return;
        moved = true; el.classList.add('is-dragging');
        el.style.setProperty('--dx', `${ox + dx}px`); el.style.setProperty('--dy', `${oy + dy}px`);
      }, on);
      const end = () => { down = false; el.classList.remove('is-dragging'); if (moved) el.dataset.dragged = '1'; };
      el.addEventListener('pointerup', end, on);
      el.addEventListener('pointercancel', end, on);
      el.addEventListener('click', () => {
        if (el.dataset.dragged) { delete el.dataset.dragged; return; }
        board?.openBottle(si, k);
      }, on);
    });
  }

  /* ---------- Prints: drag on desktop, "Put them back" resets ---------- */
  function initPrints(on) {
    $$('[data-atp-prints]').forEach(sec => {
      const area = $('[data-atp-prints-board]', sec);
      const prints = $$('[data-atp-print]', sec);
      if (sec.hasAttribute('data-atp-drag')) {
        let z = 10;
        prints.forEach(p => {
          let sx, sy, ox, oy, down = false;
          p.addEventListener('pointerdown', e => {
            if (!canDrag() || e.target.closest('[data-atp-loose]')) return;
            p.setPointerCapture(e.pointerId);
            const pr = p.getBoundingClientRect(), br = area.getBoundingClientRect();
            ox = pr.left - br.left; oy = pr.top - br.top; sx = e.clientX; sy = e.clientY;
            p.style.zIndex = ++z; p.classList.add('is-dragging'); down = true;
          }, on);
          p.addEventListener('pointermove', e => {
            if (!down) return;
            const br = area.getBoundingClientRect();
            p.style.left = `${(ox + e.clientX - sx) / br.width * 100}%`;
            p.style.top = `${(oy + e.clientY - sy) / br.height * 100}%`;
          }, on);
          const end = () => { down = false; p.classList.remove('is-dragging'); };
          p.addEventListener('pointerup', end, on);
          p.addEventListener('pointercancel', end, on);
        });
      }
      /* Place each loose bottle beside where its print rests (not where it has been dragged) */
      const placeLoose = () => {
        const W = area.clientWidth, H = area.clientHeight;
        $$('[data-atp-for]', area).forEach(l => {
          const p = prints[Number(l.dataset.atpFor)];
          if (!p) return;
          const cs = getComputedStyle(p), free = cs.position === 'absolute';
          const x = free ? parseFloat(cs.getPropertyValue('--x')) / 100 * W : p.offsetLeft;
          const y = free ? parseFloat(cs.getPropertyValue('--y')) / 100 * H : p.offsetTop;
          const pw = p.offsetWidth, ph = p.offsetHeight, bw = l.offsetWidth || 40;
          const right = l.dataset.side !== 'left';
          const left = free ? (right ? x + pw - bw * .45 : x - bw * .55) : (right ? x + pw - bw - 8 : x + 8);
          l.style.left = `${left}px`;
          l.style.top = `${y + ph * (Number(l.dataset.top) || 30) / 100}px`;
        });
      };
      placeLoose();
      const ro = new ResizeObserver(placeLoose);
      ro.observe(area);
      observers.push(ro);
      $$('img', area).forEach(img => { if (!img.complete) img.addEventListener('load', placeLoose, { ...on, once: true }); });
      $('[data-atp-reset]', sec)?.addEventListener('click', () => {
        prints.forEach(p => { p.style.left = p.style.top = p.style.zIndex = ''; });
        $$('[data-atp-loose]').forEach(l => { l.style.removeProperty('--dx'); l.style.removeProperty('--dy'); });
        placeLoose();
      }, on);
    });
  }

  /* ---------- Pouch colour swatches ---------- */
  function initPouches(on) {
    $$('[data-atp-pouch]').forEach(sec => {
      const anim = $('[data-atp-pouch-anim]', sec), name = $('[data-atp-swatch-name]', sec);
      $$('[data-atp-swatch]', sec).forEach(btn => btn.addEventListener('click', () => {
        $$('[data-atp-swatch]', sec).forEach(x => x.setAttribute('aria-pressed', String(x === btn)));
        if (anim && btn.dataset.seq) { $('span', anim).style.setProperty('--atp-seq', `url('${btn.dataset.seq}')`); anim.setAttribute('aria-label', btn.dataset.alt || ''); }
        if (name) name.textContent = btn.dataset.name || '';
      }, on));
    });
  }

  /* ---------- Closing: links that open a set on the board ---------- */
  function initClosing(on) {
    $$('[data-atp-open-set]').forEach(a => a.addEventListener('click', e => {
      if (!board) return;
      e.preventDefault();
      board.openSet(Number(a.dataset.atpOpenSet));
    }, on));
  }

  function init() {
    controller?.abort();
    observers.forEach(o => o.disconnect());
    observers = [];
    controller = new AbortController();
    const on = { signal: controller.signal };
    data = readData();
    board = initBoard($('[data-atp-board]'), on);
    initBelts(on);
    initLoose(on);
    initPrints(on);
    initPouches(on);
    initClosing(on);
    fitTapeLayer();
  }

  window.AfnanTravel = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Theme Editor: rebuild after any section changes so cross-section links stay correct */
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(type =>
    document.addEventListener(type, () => setTimeout(init, 0)));
})();
